import * as signalR from "@microsoft/signalr";
import { BASE_URL } from "common/config/baseUrl";
import store from "common/redux/store";

type Handler = (...args: any[]) => void;

const handlers = new Map<string, Set<Handler>>();
const connectedListeners = new Set<() => void>();
let connection: signalR.HubConnection | null = null;
let generation = 0; // bumped by every start/stop; a stale start gives up

/** The ACTIVE token (impersonation-aware), read fresh on every (re)connect. */
export const activeToken = (): string => (store.getState() as any)?.auth?.profileData?.token ?? "";

/** Subscribe to a hub event. Survives reconnects and restarts. Returns an unsubscribe. */
export function onHub(event: string, fn: Handler): () => void {
  let set = handlers.get(event);
  if (!set) { set = new Set(); handlers.set(event, set); }
  set.add(fn);
  connection?.on(event, fn);
  return () => { set!.delete(fn); connection?.off(event, fn); };
}

/** Called after every successful connect AND reconnect: the moment to replay. */
export function onHubConnected(fn: () => void): () => void {
  connectedListeners.add(fn);
  return () => { connectedListeners.delete(fn); };
}

function notifyConnected() {
  connectedListeners.forEach((fn) => { try { fn(); } catch { /* one bad listener must not stop others */ } });
}

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

async function keepStarting(c: signalR.HubConnection, gen: number) {
  while (gen === generation && c.state === signalR.HubConnectionState.Disconnected) {
    try {
      await c.start();
      if (gen !== generation) { await c.stop(); return; } // stopped while starting
      console.info("[taskHub] connected");
      notifyConnected();
      return;
    } catch (err) {
      console.warn("[taskHub] connect failed, retrying in 5s:", err);
      await wait(5000); // API down or token expired (401): try again
    }
  }
}

/** Start after login, and again when the signed-in identity changes (impersonation). */
export async function startTaskHub(): Promise<void> {
  await stopTaskHub();
  const gen = ++generation;
  const c = new signalR.HubConnectionBuilder()
    .withUrl(`${BASE_URL}/api/hubs/task-chat`, {
      accessTokenFactory: activeToken,
      // REQUIRED: the API's CORS allows any origin but not credentials.
      withCredentials: false,
    })
    .withAutomaticReconnect({
      // Keep trying forever, backing off to 30 s.
      nextRetryDelayInMilliseconds: (ctx) => Math.min(30000, 1000 * 2 ** Math.min(ctx.previousRetryCount, 5)),
    })
    .configureLogging(signalR.LogLevel.Warning)
    .build();

  // Admin connections also receive chat unread nudges; avoid a "No client method" warning.
  c.on("TaskChatUnreadChanged", () => {});
  handlers.forEach((set, event) => set.forEach((fn) => c.on(event, fn)));
  c.onreconnected(() => notifyConnected());
  c.onclose(() => { if (gen === generation) setTimeout(() => keepStarting(c, gen), 5000); });

  connection = c;
  await keepStarting(c, gen);
}

export async function stopTaskHub(): Promise<void> {
  generation++;
  const c = connection;
  connection = null;
  if (c) { try { await c.stop(); } catch { /* ignore */ } }
}
